import React from 'react';
import { useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../styles/home.css';
import { useTranslation } from '../utils/i18n';

const manualSections = [
  {
    title: '1. Inloggen',
    items: [
      'Log in met gebruikersnaam en wachtwoord.',
      'Kies daarna organisatie en rol en klik op Continue.'
    ]
  },
  {
    title: '2. Dashboard',
    items: [
      'Het dashboard opent na het selecteren van een hotel.',
      'Bovenaan staan hotelinformatie, weer, capaciteit en toegangsniveau.',
      'Via Quick Navigation gaat de gebruiker naar reserveringen, boxen en beschikbaarheid.'
    ]
  },
  {
    title: '3. DroneHotels bekijken',
    items: [
      'DroneHotels worden als markers op de kaart weergegeven.',
      'Klik op een marker om het dashboard van dat hotel te openen.',
      'Gebruik Map om terug te gaan naar het locatieoverzicht.'
    ]
  },
  {
    title: '4. Boxen bekijken',
    items: [
      'Klik in Quick Navigation op Active Boxes.',
      'Per box ziet de gebruiker het boxnummer en de status.',
      'Klik op een box voor details en reserveringen.'
    ]
  },
  {
    title: '5. Reserveringen beheren',
    items: [
      'Open het Reservation Panel in het hoteldashboard.',
      'Vul Target Box ID, Start Date/Time en End Date/Time in.',
      'Klik op Create Reservation om de reservering op te slaan.'
    ]
  },
  {
    title: '6. Kaartweergave',
    items: [
      'De kaart toont alle DroneHotel-locaties.',
      'De gebruiker kan inzoomen, uitzoomen en een locatie selecteren.',
      'Als geen adres bekend is, toont de app Address unavailable.'
    ]
  },
  {
    title: '7. Componenten bekijken',
    items: [
      'Klik in Quick Navigation op Components.',
      'Hier ziet de gebruiker gekoppelde onderdelen zoals camera, sensor of batterij.',
      'Een beheerder kan componentgegevens aanpassen.'
    ]
  },
  {
    title: "8. Commando's uitvoeren",
    items: [
      'Open Control Box.',
      'Kies een Target Box en een commando.',
      'Na verzenden verschijnt een bevestiging of foutmelding.'
    ]
  },
  {
    title: '9. Profiel en taal',
    items: [
      'Klik rechtsboven op het profiel-icoon.',
      'Hier staan accountgegevens, rol en organisatie.',
      'Onder Language kan de gebruiker de taal wijzigen.'
    ]
  },
  {
    title: '10. Uitloggen',
    items: [
      'Klik op Logout.',
      'De gebruiker keert terug naar het inlogscherm.'
    ]
  }
];

const errorRows = [
  {
    message: 'Unauthorized',
    meaning: 'Niet ingelogd.',
    solution: 'Opnieuw inloggen.'
  },
  {
    message: 'Box is already reserved',
    meaning: 'Box is bezet.',
    solution: 'Kies andere periode.'
  },
  {
    message: 'Hotel not found',
    meaning: 'Hotel niet gevonden.',
    solution: 'Selecteer hotel opnieuw.'
  },
  {
    message: 'Failed to create reservation',
    meaning: 'Reservering mislukt.',
    solution: 'Controleer box en tijden.'
  }
];

const tips = [
  'Controleer eerst Box Availability.',
  'Gebruik Refresh Status bij oude gegevens.',
  'Log uit na gebruik.',
  'Gebruik Chrome of Edge.'
];

function ManualPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div className="home-container manual-page">
      <nav className="navbar navbar-dark home-navbar shadow-sm mb-5">
        <div className="container-fluid px-4 d-flex align-items-center justify-content-center position-relative">
          <span className="navbar-brand mb-0 h1 fw-bold fs-4">Drone Hotel</span>
          <div className="d-flex gap-2 position-absolute end-0 manual-navbar-actions">
            <button className="btn btn-outline-light rounded-pill px-4" onClick={() => navigate('/')}>{t('Home')}</button>
            <button className="btn btn-outline-light rounded-pill px-4" onClick={() => navigate('/map')}>{t('Map')}</button>
            <button className="btn profile-icon-button" onClick={() => navigate('/profile')} aria-label={t('Open profile')}>
              <i className="bi bi-person-circle"></i>
            </button>
          </div>
        </div>
      </nav>

      <main className="container manual-container">
        <section className="manual-header">
          <h1>Eindgebruikershandleiding</h1>
          <p>
            Korte uitleg voor het gebruiken van de DroneHotel-app.
          </p>
        </section>

        <section className="manual-grid" aria-label="Handleiding onderdelen">
          {manualSections.map((section) => (
            <article className="manual-card" key={section.title}>
              <h2>{section.title}</h2>
              <ul>
                {section.items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </article>
          ))}
        </section>

        <section className="manual-support">
          <div>
            <h2>Veelvoorkomende foutmeldingen</h2>
          </div>
          <div className="manual-table-wrap">
            <table className="manual-error-table">
              <thead>
                <tr>
                  <th>Foutmelding</th>
                  <th>Betekenis</th>
                  <th>Oplossing</th>
                </tr>
              </thead>
              <tbody>
                {errorRows.map((row) => (
                  <tr key={row.message}>
                    <td>{row.message}</td>
                    <td>{row.meaning}</td>
                    <td>{row.solution}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="manual-support">
          <div>
            <h2>Tips voor gebruikers</h2>
          </div>
          <ul>
            {tips.map((tip) => (
              <li key={tip}>{tip}</li>
            ))}
          </ul>
        </section>
      </main>
    </div>
  );
}

export default ManualPage;
